import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DAILY_LIFE_SECTION_TITLE,
  HOMEPAGE_LIFE_SLIDES,
  assignDailyLifeSlidePhotos,
} from '../../data/homepageLifeSlides';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function DailyLifeCarousel() {
  const navigate = useNavigate();
  const slides = HOMEPAGE_LIFE_SLIDES;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidePhotos, setSlidePhotos] = useState(() => Array(slides.length).fill(null));

  const slideCount = slides.length;
  const prevIndex = (currentIndex - 1 + slideCount) % slideCount;
  const nextIndex = (currentIndex + 1) % slideCount;
  const active = slides[currentIndex];

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/social-feed?paginate=1&offset=0&limit=60`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((data) => {
        if (!cancelled) setSlidePhotos(assignDailyLifeSlidePhotos(data.items));
      })
      .catch(() => {
        if (!cancelled) setSlidePhotos(Array(slideCount).fill(null));
      });
    return () => {
      cancelled = true;
    };
  }, [slideCount]);

  const goPrev = () => setCurrentIndex((i) => (i - 1 + slideCount) % slideCount);
  const goNext = () => setCurrentIndex((i) => (i + 1) % slideCount);

  return (
    <section className="daily-life-section">
      <div className="container">
        <div className="daily-life-section-header">
          <h2 className="daily-life-section-title">{DAILY_LIFE_SECTION_TITLE}</h2>
        </div>

        <div className="daily-life-layout">
          <div className="daily-life-carousel-wrap">
            <div className="image-carousel-container daily-life-carousel-container">
              <div className="image-carousel">
                {slides.map((slide, index) => {
                  let positionClass = 'hidden-image';
                  if (index === currentIndex) positionClass = 'active-image';
                  else if (index === prevIndex) positionClass = 'prev-image';
                  else if (index === nextIndex) positionClass = 'next-image';

                  const photo = slidePhotos[index];
                  const imageSrc = slide.staticImage
                    ? slide.staticImage
                    : photo
                      ? resolveMediaUrl(photo.imageUrl)
                      : null;

                  return (
                    <div
                      key={slide.id}
                      className={`carousel-image ${positionClass}`}
                      onClick={() => setCurrentIndex(index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setCurrentIndex(index);
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={slide.title}
                    >
                      {imageSrc ? (
                        <div className="living-choices-image">
                          <img
                            src={imageSrc}
                            alt={slide.title}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      ) : (
                        <div
                          className={`living-choices-image-placeholder ${slide.className}`}
                        >
                          <span>{slide.imageLabel}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className="carousel-arrow left-arrow"
                onClick={goPrev}
                aria-label="Előző"
              >
                ‹
              </button>
              <button
                type="button"
                className="carousel-arrow right-arrow"
                onClick={goNext}
                aria-label="Következő"
              >
                ›
              </button>
            </div>
          </div>

          <div className="daily-life-panel">
            <span className="daily-life-panel__label">{active.label}</span>
            <h3 className="daily-life-panel__title">{active.title}</h3>
            <p className="daily-life-panel__description">{active.description}</p>
            <ul className="daily-life-bullets">
              {active.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <button
              type="button"
              className="daily-life-panel__cta"
              onClick={() => navigate(active.ctaPath)}
            >
              {active.ctaLabel} <span aria-hidden>→</span>
            </button>
            <div className="daily-life-dots" role="tablist" aria-label="Élet a Napvirágban">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  role="tab"
                  aria-selected={index === currentIndex}
                  className={`daily-life-dot ${index === currentIndex ? 'is-active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default DailyLifeCarousel;
